import React from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { loadGMaps } from '@/utils/load-gmaps';
import { IconSaxIcon } from '../icon';
import { composeClassName } from '@/utils/util_helpers';
import { useWindowDimensions } from '@/hooks/use-window-dimensions';

interface ColocationMapProps {
  id: string;
  initialCoordinates: {
    lat: number;
    lng: number;
  };
  markers?: Array<{
    lat: number;
    lng: number;
    content?: string;
  }>;
  zoom?: number;
  maxZoom?: number;
  height?: string | number;
  width?: string | number;
  className?: string;
  address?: string;
  DisplayHeading?: boolean;
  styles?: Array<{
    featureType?: string;
    elementType?: string;
    stylers: Array<Record<string, any>>;
  }>;
}

export const ColocationMap: React.FC<ColocationMapProps> = ({
  id,
  initialCoordinates,
  markers = [],
  zoom = 12,
  maxZoom = 18,
  height = '400px',
  width = '100%',
  className = '',
  address = '',
  DisplayHeading = true,
  styles: stylesProp,
}) => {
  const { t } = useTranslation();
  const { screenIsLargeOrEqualThan } = useWindowDimensions();
  const isMobile = !screenIsLargeOrEqualThan('sm');

  const styles = React.useMemo(
    () => stylesProp ?? [
      {
        featureType: 'all',
        elementType: 'all',
        stylers: [{ saturation: -100 }, { lightness: 0 }],
      },
    ],
    [stylesProp]
  );

  const mapElementId = `${id}-${React.useId().replace(/:/g, '')}`;

  if (!initialCoordinates.lat || !initialCoordinates.lng) {
    return null;
  }

  React.useEffect(() => {
    const abortController = new AbortController();
    let mapInstance: any = null;

    (async () => {
      try {
        const GMaps = await loadGMaps();
        if (abortController.signal.aborted) return;
        const mapElement = document.getElementById(mapElementId);
        if (!mapElement) return;

        mapInstance = new GMaps({
          el: `#${mapElementId}`,
          lat: initialCoordinates.lat,
          lng: initialCoordinates.lng,
          zoom,
          styles,
          scrollwheel: true,
          fullscreenControl: false,
          maxZoom,
        });

        markers.forEach((marker) => {
          if (!marker.lat || !marker.lng) return;
          if (marker.content) {
            mapInstance.drawOverlay(marker);
          } else {
            mapInstance.addMarker(marker);
          }
        });

        if (markers.length === 1) {
          mapInstance.fitLatLngBounds(markers);
          mapInstance.panTo(markers[0]);
          mapInstance.setZoom(13);
        } else if (markers.length > 1) {
          mapInstance.fitLatLngBounds(markers);
        }
      } catch (err) {
        console.error('ColocationMap failed to load', err);
      }
    })();

    return () => {
      abortController.abort();
      if (mapInstance?.map) {
        mapInstance.map.setCenter(null);
      }
    };
  }, [mapElementId, initialCoordinates.lat, initialCoordinates.lng, markers, zoom, maxZoom, styles]);

  if (!DisplayHeading) {
    return (
      <div
        id={mapElementId}
        style={{
          height,
          width,
        }}
      />
    );
  }

  return (
    <div style={{ borderColor: '#E8E8E8' }} className="border-t border-solid pt-7">
      <Box className={className}>
        <h2
          className={composeClassName(
            'font-sofia-pro mb-4  font-bold leading-[30px] text-N900',
            isMobile ? 'text-lead1' : 'text-[22px]'
          )}
        >
          {t('js.components.house_tabs.location')}
        </h2>
        <div style={{ fontSize: '16px' }} className="lead2 mb-2 flex items-center gap-2 text-N900">
          {IconSaxIcon({ name: 'location', size: 15, strokeWidth: 1.5 })}
          <span className="font-sans font-normal text-N900">{address}</span>
        </div>
        <div
          id={mapElementId}
          style={{
            height,
            width,
            borderRadius: '8px',
          }}
        />
      </Box>
    </div>
  );
};
